import React from "react";
import { Link } from "react-router-dom";
import { getBrand } from "../../utils/branding";
import { getLegalContent } from "../../utils/legalContent";
import Logo from "../../components/Logo";
import Footer from "../../components/Footer";
import { ArrowLeft } from "lucide-react";

export default function Security() {
  const brand = getBrand();
  const c = getLegalContent();

  return (
    <div className="au-root">
      <header style={hdr}>
        <Link to="/" style={{ textDecoration: "none" }}><Logo size="medium" /></Link>
        <Link to="/" className="au-pill au-pill-glass au-pill-sm">
          <ArrowLeft /> Back to Home
        </Link>
      </header>

      <div className="au-container" style={{ maxWidth: 820 }}>
        <div className="au-glass au-pad-lg" style={{ marginBottom: 56 }}>
          <div className="au-eyebrow" style={{ marginBottom: 14 }}>
            SECURITY · LAST UPDATED {c.lastUpdated.toUpperCase()}
          </div>
          <h1 style={h1}>Security <span style={{ color: "var(--au-accent-ink)" }}>at {c.name}</span></h1>
          <p className="au-lead" style={{ marginTop: 16 }}>
            Security isn't a feature — it's our foundation. Here's how we protect your data.
          </p>

          <S n="1" t="Infrastructure Security">
            <ul style={ul}>
              <li><strong>AWS Mumbai region</strong> — data stays in India</li>
              <li><strong>Isolated network:</strong> Backend on private port (loopback only)</li>
              <li><strong>Firewall:</strong> Only HTTPS (443) and SSH (22) exposed</li>
              <li><strong>Auto-scaling:</strong> Resilient to traffic spikes</li>
            </ul>
          </S>

          <S n="2" t="Encryption">
            <ul style={ul}>
              <li><strong>In transit:</strong> TLS 1.3 (HTTPS) — A+ SSL Labs grade</li>
              <li><strong>At rest:</strong> AES-256 encryption for sensitive data</li>
              <li><strong>Database:</strong> Encrypted volumes</li>
              <li><strong>Backups:</strong> Encrypted, off-site storage</li>
            </ul>
          </S>

          <S n="3" t="Authentication">
            <ul style={ul}>
              <li><strong>Passwordless:</strong> No passwords to leak — email-based codes only</li>
              <li><strong>JWT tokens:</strong> Industry-standard session management</li>
              <li><strong>Code expiry:</strong> 15 minutes, single-use</li>
              <li><strong>Rate limiting:</strong> 3 register attempts/hour per IP</li>
            </ul>
          </S>

          <S n="4" t="Application Security">
            <ul style={ul}>
              <li>Input validation on every endpoint</li>
              <li>SQL injection prevention (ORM-based queries)</li>
              <li>XSS prevention (React auto-escaping)</li>
              <li>CSRF protection on state-changing operations</li>
              <li>Regular dependency updates</li>
            </ul>
          </S>

          <S n="5" t="Data Protection">
            <ul style={ul}>
              {c.kycRequired && <li>KYC documents deleted after verification (UIDAI compliant)</li>}
              <li>No third-party tracking or advertising cookies</li>
              <li>Server logs anonymized after 90 days</li>
              <li>Right to data deletion: email <a href={`mailto:${c.privacyEmail}`} style={link}>{c.privacyEmail}</a></li>
            </ul>
          </S>

          <S n="6" t="Responsible Disclosure">
            <p>Found a security vulnerability? Please report responsibly:</p>
            <ul style={ul}>
              <li>Email: <a href={`mailto:security@${c.parentDomain}`} style={link}>security@{c.parentDomain}</a></li>
              <li>Don't access user data beyond what's needed to demonstrate the issue</li>
              <li>Allow 90 days for fix before public disclosure</li>
              <li>We acknowledge researchers in our security hall of fame</li>
            </ul>
          </S>

          <S n="7" t="Compliance">
            <ul style={ul}>
              <li><strong>IT Act 2000 (India):</strong> Working towards full compliance</li>
              <li><strong>DPDPA 2023 (India):</strong> Working towards full compliance</li>
              <li><strong>GDPR (EU):</strong> Working towards full compliance</li>
              <li><strong>OWASP Top 10:</strong> Following secure development practices</li>
              {c.kycRequired && <li><strong>Aadhar handling:</strong> Following UIDAI guidelines (encrypted, auto-deleted)</li>}
              {c.paymentSupported && <li><strong>PCI DSS:</strong> Card data handled by certified payment processors</li>}
            </ul>
          </S>

          <S n="8" t="Incident Response">
            <p>In case of a security incident:</p>
            <ul style={ul}>
              <li>Affected users notified within 72 hours</li>
              <li>Public disclosure on status page</li>
              <li>Detailed post-mortem published</li>
              <li>Steps taken to prevent recurrence</li>
            </ul>
          </S>

          <S n="9" t="Contact">
            <p>Security questions? Email <a href={`mailto:security@${c.parentDomain}`} style={link}>security@{c.parentDomain}</a></p>
            <p style={metaMuted}>{c.fullName} · {c.address}</p>
          </S>

        </div>
      </div>
      <Footer />
    </div>
  );
}

function S({ n, t, children }) {
  return (
    <div style={section}>
      <h2 style={h2}>
        <span style={{ color: "var(--au-accent-ink)", marginRight: 10 }}>{n}.</span>{t}
      </h2>
      <div style={body}>{children}</div>
    </div>
  );
}

const hdr = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, maxWidth: 1200, margin: "0 auto", padding: "28px 24px 8px" };
const h1 = { fontSize: 42, margin: 0 };
const section = { marginTop: 36, paddingTop: 28, borderTop: "1px solid var(--au-hair)" };
const h2 = { fontSize: 20, marginBottom: 16, fontWeight: 700 };
const body = { color: "var(--au-text-2)", fontSize: 14, lineHeight: 1.8 };
const ul = { margin: "8px 0", paddingLeft: 22 };
const link = { color: "var(--au-accent-ink)", textDecoration: "none", fontWeight: 600 };
const metaMuted = { fontSize: 13, color: "var(--au-muted)", marginTop: 8 };
const copyright = { marginTop: 48, paddingTop: 24, borderTop: "1px solid var(--au-hair)", fontSize: 12, color: "var(--au-muted)", textAlign: "center" };
